/**
 * Navigation for the links rendered by `snippets/obfuscated-link.liquid`.
 *
 * Those carry their destination base64-encoded in `data-o` instead of an `<a href>`, so a crawler
 * never follows them, and no URL path shows in the HTML (cf. `.docs/seo-guidelines.md`). One set of delegated listeners on the document covers every
 * one of them, including the ones a morph or a Section Rendering API swap inserts later: there is no
 * per-element hydration and nothing to re-arm.
 */

import { decodeObfuscatedUrl } from '@theme/utilities';

const SELECTOR = '.obfuscated-link[data-o]';

/**
 * The obfuscated link an event comes from, or `null` when the event belongs to something else.
 *
 * A nested control wins over the link: an event that already has a handler of its own must not
 * navigate on top of it.
 *
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function linkFrom(event) {
  const target = event.target;
  if (!(target instanceof Element)) return null;

  const nearest = target.closest(`${SELECTOR}, a[href], button, input, select, textarea, summary`);
  if (!(nearest instanceof HTMLElement) || !nearest.matches(SELECTOR)) return null;

  return nearest;
}

/**
 * @param {HTMLElement} link
 * @param {boolean} newTab
 */
function navigate(link, newTab) {
  const url = decodeObfuscatedUrl(link.dataset.o);
  if (!url) return;

  if (newTab || link.dataset.target === '_blank') {
    window.open(url, '_blank');
  } else {
    window.location.assign(url);
  }
}

document.addEventListener('click', (event) => {
  const link = linkFrom(event);
  if (!link) return;

  event.preventDefault();
  navigate(link, event.metaKey || event.ctrlKey || event.shiftKey);
});

/* Middle click. `click` never fires for button 1, `auxclick` is the only event that reports it. */
document.addEventListener('auxclick', (event) => {
  if (event.button !== 1) return;

  const link = linkFrom(event);
  if (!link) return;

  event.preventDefault();
  navigate(link, true);
});

/* `role="link"` is activated by Enter only — Space is a button affordance and must stay inert. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;

  const link = linkFrom(event);
  if (!link) return;

  event.preventDefault();
  navigate(link, event.metaKey || event.ctrlKey || event.shiftKey);
});
